'use client'

/**
 * Smart skeleton fallback for RecipientView - shows cached data when available.
 * Matches the current SuspenseRecipientViewV2 / RecipientDetail compound component layout.
 */
import React from 'react'

import { Building2, ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'

import { Skeleton } from '@/components/common/Skeleton'

import { useSkeletonRecipientData } from '@/hooks/usaspending'

interface RecipientViewSkeletonProps {
  id: string
}

export function RecipientViewSkeleton({ id }: RecipientViewSkeletonProps) {
  const cached = useSkeletonRecipientData(id)

  return (
    <div className="flex w-full flex-col items-center pb-8">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <header className="w-full border-b border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-950">
        <div className="mx-auto max-w-7xl px-4 py-3 md:px-8">
          {/* Breadcrumbs */}
          <nav className="mb-2 flex items-center gap-1.5 text-[10px] font-medium text-primary-400 dark:text-primary-500">
            <Link href="/" className="transition-colors hover:text-accent-500">
              Home
            </Link>
            <ChevronRight className="h-3 w-3" />
            <Link
              href="/recipients/search"
              className="transition-colors hover:text-accent-500"
            >
              Recipients
            </Link>
            <ChevronRight className="h-3 w-3" />
            {cached?.name ? (
              <span className="truncate text-primary-600 dark:text-primary-400">
                {cached.name}
              </span>
            ) : (
              <Skeleton className="h-3 w-32" />
            )}
          </nav>

          {/* Main row: icon + name/badges | identifiers */}
          <div className="flex items-start justify-between gap-4">
            {/* Left */}
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-800">
                <Building2 className="h-5 w-5 text-primary-400 dark:text-primary-500" />
              </div>
              <div className="min-w-0 flex-1">
                {cached?.name ? (
                  <h1 className="mb-1.5 text-lg font-bold leading-tight tracking-tight text-primary-900 sm:text-xl md:text-2xl dark:text-primary-50">
                    {cached.name}
                  </h1>
                ) : (
                  <Skeleton className="mb-1.5 h-6 w-64" />
                )}
                {/* Cert badge placeholders */}
                <div className="flex gap-1.5">
                  <Skeleton className="h-5 w-12" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-14" />
                </div>
              </div>
            </div>

            {/* Right: identifiers — matches RecipientHeaderSkeleton */}
            <div className="hidden shrink-0 flex-col gap-0.5 sm:flex">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-3 w-10" />
                  <Skeleton className="h-5 w-24" />
                </div>
              ))}
            </div>
          </div>

          {/* KPI strip + FY filter — matches KPIStripSkeleton */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-primary-100 pt-3 dark:border-primary-800">
            <div className="flex flex-wrap items-center gap-4 divide-x divide-primary-200 dark:divide-primary-700">
              <div className="flex flex-col">
                <Skeleton className="mb-1 h-3 w-20" />
                <Skeleton className="h-5 w-16" />
              </div>
              {[1, 2].map((i) => (
                <div key={i} className="flex flex-col pl-4">
                  <Skeleton className="mb-1 h-3 w-20" />
                  <Skeleton className="h-5 w-16" />
                </div>
              ))}
            </div>
            {/* FY picker placeholder — matches actual h-8 w-28 */}
            <Skeleton className="h-8 w-28" />
          </div>
        </div>
      </header>

      {/* ── Quick Actions bar ───────────────────────────────────────── */}
      <div className="w-full border-b border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-950">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          {/* py-1.5 matches RecipientQuickActions */}
          <div className="flex items-center gap-1 overflow-x-auto py-1.5">
            <Skeleton className="h-8 w-28 rounded-md" />
            <Skeleton className="h-8 w-24 rounded-md" />
            <Skeleton className="h-8 w-36 rounded-md" />
            <Skeleton className="h-8 w-24 rounded-md" />
          </div>
        </div>
      </div>

      {/* ── Minimap tabs ────────────────────────────────────────────── */}
      <div className="w-full border-b border-primary-200 bg-white/95 backdrop-blur-sm dark:border-primary-800 dark:bg-primary-950/95">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          {/* 10 tabs matching SECTIONS in RecipientMinimap; py-2 matches real nav */}
          <div className="flex gap-1 overflow-x-auto py-2">
            {[90, 88, 86, 82, 92, 94, 100, 86, 82, 78].map((w, i) => (
              <div
                key={i}
                className="h-[30px] shrink-0 animate-pulse rounded-full bg-primary-300 dark:bg-primary-700"
                style={{ width: w }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Main content ────────────────────────────────────────────── */}
      <main className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8">
        <div className="space-y-6">
          {/* Insights panel */}
          <div className="rounded-xl border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900">
            <div className="flex items-center gap-3 border-b border-primary-100 px-4 py-3 dark:border-primary-800">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-4 w-28" />
            </div>
            <div className="flex gap-3 overflow-x-auto p-3 sm:p-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="min-w-[180px] flex-1 rounded-lg border border-primary-100 p-3 dark:border-primary-800"
                >
                  <Skeleton className="mb-2 h-3 w-24" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="mt-1 h-4 w-3/4" />
                </div>
              ))}
            </div>
          </div>

          {/* Analytics sections grid - gap-6 matches RecipientDetail.Sections className="gap-6" */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Cash flow — full width */}
            <SectionSkeleton
              title="Award Cash Flow"
              height={280}
              className="md:col-span-2"
            />
            {/* Award Mix + Top Agencies */}
            <SectionSkeleton title="Award Mix" height={180} bars={0} />
            <SectionSkeleton title="Top Agencies" height={220} bars={5} />
            {/* Industry */}
            <SectionSkeleton title="Industry Codes" height={280} bars={5} />
            {/* Geography — full width */}
            <SectionSkeleton
              title="Geography"
              height={280}
              bars={0}
              className="md:col-span-2"
            />
            {/* Set-asides + Competition */}
            <SectionSkeleton title="Set-Asides" height={180} bars={4} />
            <SectionSkeleton title="Competition" height={180} bars={4} />
            {/* Contract Sizes */}
            <SectionSkeleton title="Contract Sizes" height={200} bars={0} />
            {/* Officers — full width */}
            <SectionSkeleton
              title="Officers"
              height={100}
              bars={2}
              className="md:col-span-2"
            />
          </div>

          {/* Recent Awards */}
          <SectionSkeleton title="Recent Awards" height={200} bars={5} />
        </div>
      </main>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface SectionSkeletonProps {
  title: string
  height: number
  bars?: number
  className?: string
}

function SectionSkeleton({
  title,
  height,
  bars = 3,
  className,
}: SectionSkeletonProps) {
  return (
    <div
      className={twMerge(
        'rounded-xl border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
        className,
      )}
    >
      {/* Header — matches SectionCard: px-3 py-2 sm:px-4 sm:py-2.5, title text-xs font-bold uppercase */}
      <div className="flex items-center border-b border-primary-100 px-3 py-2 sm:px-4 sm:py-2.5 dark:border-primary-800">
        <span className="text-xs font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
          {title}
        </span>
      </div>
      {/* Content */}
      <div
        className="animate-pulse p-3 sm:p-4"
        style={{ height, overflow: 'hidden' }}
      >
        {bars > 0 ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: bars }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <div
                    className="h-3.5 animate-pulse rounded bg-primary-300 dark:bg-primary-700"
                    style={{ width: `${62 - i * 8}%` }}
                  />
                  <Skeleton className="h-3.5 w-14 rounded" />
                </div>
                <Skeleton className="h-2 w-full rounded-full" />
              </div>
            ))}
          </div>
        ) : (
          <Skeleton className="h-full w-full rounded-lg" />
        )}
      </div>
    </div>
  )
}
